import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { 
  Loader2, 
  Eye, 
  EyeOff, 
  ArrowRight,
  Lock
} from "lucide-react";
import { toast } from "sonner";
import { translateAuthError } from "@/utils/auth-errors";
import logoAsset from "@/assets/logo.png.asset.json";

export const Route = createFileRoute("/entrar")({
  component: Login,
});

function Login() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { authState, profile } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch() as { redirect?: string };

  useEffect(() => {
    if (authState === 'autenticado' && profile?.status === 'aprovado') {
      const redirectTo = search.redirect || '/';
      navigate({ to: redirectTo as any, replace: true });
    }
  }, [authState, profile, navigate, search.redirect]);

  if (authState === 'carregando') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      setError(translateAuthError(authError.message));
      setLoading(false);
      return;
    }

    toast.success("Login realizado com sucesso!");
  };

  const handleResetPassword = async () => {
    const email = prompt("Digite seu e-mail para recuperar a senha:");
    if (!email) return;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("E-mail de recuperação enviado!");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      <Helmet>
        <title>Entrar | Lista de Fornecedores Gopage</title>
        <meta name="description" content="Acesse sua conta para visualizar os contatos diretos dos melhores fornecedores e atacadistas do Brasil." />
        <link rel="canonical" href="https://spark-smile-hub.lovable.app/entrar" />
      </Helmet>
      
      {/* Painel Esquerdo */}
      <div className="flex md:flex-col justify-between p-6 md:p-12 bg-[#0C1A12] text-white w-full md:w-1/3 min-h-[120px] md:min-h-screen relative overflow-hidden">
        <Link to="/" className="relative z-10">
          <img src={logoAsset.url} alt="Gopage" className="h-10 w-10 md:h-12 md:w-12 rounded-xl" />
        </Link>
        
        <div className="hidden md:block relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C6F764]/10 rounded-full text-xs font-bold uppercase tracking-widest text-[#C6F764] mb-6 border border-[#C6F764]/20">
            <Lock className="w-3 h-3" /> Acesso Restrito
          </div>
          <h2 className="text-4xl font-bold mb-6 leading-tight">
            Seu diretório estratégico de <span className="text-[#C6F764]">fornecedores</span>.
          </h2>
          <p className="text-lg opacity-80 leading-relaxed">
            Faça login para acessar os contatos diretos de fornecedores e atacadistas de todo o Brasil.
          </p>
        </div>
        
        <div className="hidden md:block text-sm opacity-40 relative z-10">© 2026 Gopage</div>
      </div>

      {/* Lado Direito - Formulário */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="mb-10">
            <h1 className="text-3xl font-bold mb-2">Bem-vindo de volta</h1>
            <p className="text-muted-foreground leading-relaxed">
              Informe seu e-mail e senha para acessar sua conta.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground block">
                E-mail
              </label>
              <input 
                name="email"
                type="email"
                required
                className="w-full px-4 py-4 bg-[#F7F9F5] border border-border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all"
                placeholder="exemplo@email.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  Senha
                </label>
                <button 
                  type="button"
                  onClick={handleResetPassword}
                  className="text-xs font-bold text-[#0C1A12] hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
              <div className="relative">
                <input 
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full px-4 py-4 bg-[#F7F9F5] border border-border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all"
                  placeholder="••••••••"
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0C1A12]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-2xl text-destructive text-sm font-medium">
                {error}
              </div>
            )}

            <Button 
              disabled={loading}
              variant="onLight"
              className="w-full py-7 font-bold rounded-2xl transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 group"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Entrar na minha conta
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-10 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              Ainda não é um cliente? <Link to="/cadastro" className="text-[#0C1A12] font-bold hover:underline">Criar meu acesso agora</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
